/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { buildInventorySummary } from './inventorySummary';
import { inventoryAccount } from './inventoryAccount';
import { machineTypes, machineStateLabel } from './inventoryMachines';
import InventoryMachineDetails from './InventoryMachineDetails';
import InventoryResourceDetails from './InventoryResourceDetails';
import InventoryDownload from './InventoryDownload';
import SearchableSelect, { searchText } from './SearchableSelect';
import { captureKey, captureParams, validateCapture } from '../../services/inventoryIdentity';

import { 
  Database, 
  RefreshCw, 
  Search, 
  Globe, 
  Filter, 
  Layers, 
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export function getMexicoToday() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date());
  const value = (type) => parts.find((part) => part.type === type).value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

export default function InventarioNubes() {
  const { hasPermission } = useAuth();
  const canViewInventory = hasPermission('ops.inventario.view');

  const [loading, setLoading] = useState(true);
  const [loadingInventarios, setLoadingInventarios] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [resumenError, setResumenError] = useState('');
  const [inventariosError, setInventariosError] = useState('');
  const [detalleError, setDetalleError] = useState('');
  const [hasDateRange, setHasDateRange] = useState(false);
  const inventoryRequest = useRef(0);
  const detailRequest = useRef(0);

  const [resumen, setResumen] = useState([]);
  const [inventarios, setInventarios] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [catalogVersion, setCatalogVersion] = useState(0);

  const [selectedInventario, setSelectedInventario] = useState(null);

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [expandedServices, setExpandedServices] = useState({});
  const [expandedMachines, setExpandedMachines] = useState({});
  const detailView = useRef(null);
  const returnFocus = useRef(null);

  const [filters, setFilters] = useState(() => {
    const today = getMexicoToday();
    return { proveedor: '', cliente: '', fechaInicio: today, fechaFin: today };
  });

  const cardClasses =
    'bg-[#181024]/90 border border-violet-300/10';
  const inputClasses =
    'w-full min-w-0 rounded-xl border border-white/15 bg-black/20 px-3 py-2.5 text-sm text-white outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 [color-scheme:dark]';

  // =========================================================
  // LOADERS
  // =========================================================

  useEffect(() => {
    if (canViewInventory) {
      initialLoad();
    }
  }, [canViewInventory]);

  useEffect(() => {
    if (!canViewInventory || !catalogOpen) return;
    const controller = new AbortController();
    setClientOptions([]);
    setCatalogError('');
    setCatalogLoading(true);
    const params = new URLSearchParams();
    if (filters.proveedor) params.set('proveedor', filters.proveedor);
    if (filters.fechaInicio) params.set('fecha_inicio', filters.fechaInicio);
    if (filters.fechaFin) params.set('fecha_fin', filters.fechaFin);
    const load = async () => {
      try {
        const response = await fetch(`${API_URL}/v2/inventario/historico?${params}`, { signal: controller.signal });
        const rows = await response.json();
        if (!response.ok) throw new Error(rows.detail || 'No se pudieron consultar los clientes disponibles.');
        if (!Array.isArray(rows)) throw new Error('El catalogo de clientes tiene un formato invalido.');
        const unique = new Map();
        const order = ['AWS', 'AZURE', 'HUAWEI', 'OCI', 'GCP'];
        rows.forEach(row => {
          const provider = String(row.proveedor || '').toUpperCase();
          const name = row.nombre_cliente || row.nombre_cliente_legacy;
          if (!name || !order.includes(provider)) return;
          if (filters.proveedor && searchText(provider) !== searchText(filters.proveedor)) return;
          const id = JSON.stringify([provider, name]);
          unique.set(id, { id, label: `${provider === 'AZURE' ? 'Azure' : provider === 'HUAWEI' ? 'Huawei' : provider}: ${name}`,
            name, provider: row.proveedor });
        });
        const options = [...unique.values()].sort((a, b) =>
          order.indexOf(a.provider.toUpperCase()) - order.indexOf(b.provider.toUpperCase())
          || a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
        if (!controller.signal.aborted) setClientOptions(options);
      } catch (error) {
        if (!controller.signal.aborted) setCatalogError(error.message);
      } finally {
        if (!controller.signal.aborted) setCatalogLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [canViewInventory, catalogOpen, filters.proveedor, filters.fechaInicio, filters.fechaFin, catalogVersion]);

  useEffect(() => {
    if (selectedInventario) {
      detailView.current?.focus();
    } else {
      returnFocus.current?.focus({ preventScroll: true });
    }
  }, [selectedInventario]);

  const initialLoad = async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadResumen(),
        loadInventarios()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadResumen = async () => {
    setResumenError('');
    try {
      const response = await fetch(`${API_URL}/inventario/resumen`);
      if (!response.ok) throw new Error('No se pudo consultar el resumen de inventarios.');
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('El resumen de inventarios tiene un formato invalido.');
      setResumen(data.some(item => item.proveedor === 'OCI') ? data : [
        ...data, { proveedor: 'OCI', total_snapshots: 0, ultima_actualizacion: null },
      ]);
    } catch (error) {
      console.error('Error loading resumen:', error);
      setResumen([]);
      setResumenError(error.message);
    }
  };

  const loadInventarios = async () => {
    const request = ++inventoryRequest.current;
    setLoadingInventarios(true);
    setInventariosError('');
    setInventarios([]);
    const today = getMexicoToday();
    setHasDateRange(Boolean(
      (filters.fechaInicio || filters.fechaFin)
      && !(filters.fechaInicio === today && filters.fechaFin === today)
    ));

    try {
      const params = new URLSearchParams();

      if (filters.proveedor || selectedClient?.provider) {
        params.append('proveedor', filters.proveedor || selectedClient.provider);
      }

      if (filters.cliente) {
        params.append('cliente', filters.cliente);
      }

      if (filters.fechaInicio) {
        params.append('fecha_inicio', filters.fechaInicio);
      }

      if (filters.fechaFin) {
        params.append('fecha_fin', filters.fechaFin);
      }

      // Consulta al endpoint v2
      const response = await fetch(
        `${API_URL}/v2/inventario/historico?${params}`
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudieron consultar los inventarios.');
      if (!Array.isArray(data)) throw new Error('La lista de inventarios tiene un formato invalido.');
      if (request === inventoryRequest.current) setInventarios(selectedClient
        ? data.filter(row => searchText(row.proveedor) === searchText(selectedClient.provider)
          && (row.nombre_cliente || row.nombre_cliente_legacy) === selectedClient.name)
        : data);

    } catch (error) {
      console.error('Error loading inventarios:', error);
      if (request === inventoryRequest.current) setInventariosError(error.message);
    } finally {
      if (request === inventoryRequest.current) setLoadingInventarios(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setCatalogVersion(version => version + 1);

    try {
      await Promise.all([
        loadResumen(),
        loadInventarios()
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const loadDetalle = async (inv) => {
    if (!selectedInventario) returnFocus.current = document.activeElement;
    const request = ++detailRequest.current;
    setSelectedInventario(inv);
    setDetalle(null);
    setDetalleError('');
    setLoadingDetalle(true);

    try {
      const response = await fetch(`${API_URL}/v2/inventario/detalle/${inv.id}?${captureParams(inv)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo leer el inventario desde GCS.');
      if (!data.metadata || !data.por_servicio) throw new Error('El detalle del inventario tiene un formato invalido.');
      validateCapture(inv, data.metadata);
      if (request !== detailRequest.current) return;
      setDetalle(data);
      setExpandedServices({});
      setExpandedMachines({});
    } catch (error) {
      console.error('Error loading detalle:', error);
      if (request === detailRequest.current) setDetalleError(error.message);
    } finally {
      if (request === detailRequest.current) setLoadingDetalle(false);
    }
  };

  const closeDetalle = () => {
    detailRequest.current += 1;
    setSelectedInventario(null);
    setDetalle(null);
  };

  // =========================================================
  // HELPERS
  // =========================================================

  const getProveedorIcon = (proveedor) => {
    switch (proveedor?.toUpperCase()) {
      case 'AWS':
        return '/assets/icons/aws.png';
      case 'AZURE':
        return '/assets/icons/azure.png';
      case 'GCP':
        return '/assets/icons/gcp.png';
      case 'HUAWEI':
        return '/assets/icons/huawei.png';
      case 'OCI':
        return '/assets/icons/oracle.svg';
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    return new Date(dateString).toLocaleDateString('es-MX', {
      timeZone: 'America/Mexico_City',
      hourCycle: 'h23',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleService = (service) => {
    setExpandedServices((prev) => ({
      ...prev,
      [service]: !prev[service]
    }));
  };

  // =========================================================
  // ACCESO
  // =========================================================
  if (!canViewInventory) {
    return (
      <div role="alert" className="rounded-2xl border border-violet-300/10 bg-[#10091c] text-violet-200 p-6">
        No tienes permisos para consultar Inventario Nubes.
      </div>
    );
  }

  // =========================================================
  // CARGA
  // =========================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-[#07010f] flex items-center justify-center">
        <div className="flex items-center gap-4 text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8644ff]" />
          <div>
            <p className="text-xl font-semibold">
              Cargando Inventario Cloud
            </p>
            <p className="text-sm opacity-60 mt-1">
              Inicializando inventarios históricos...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl border border-violet-300/10 bg-[#10091c] text-white">
      {/* Background */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-[700px] h-[700px] bg-[#8644ff]/15 blur-3xl rounded-full" />
      </div>

      {/* Header */}
      <div className="relative z-10 border-b border-white/10">
        <div className="px-4 py-5 sm:px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-[#8644ff]/20 border border-[#8644ff]/30">
              <Layers className="w-6 h-6 text-[#cbb5f9]" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight text-violet-50">
                  Inventario de nubes
                </h2>
              </div>
              <p className="text-sm text-white/60 mt-1">
                Consulta recursos y ejecuciones de tus cuentas cloud.
              </p>
            </div>
          </div>

          {!selectedInventario && <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`
              px-4 py-2.5 rounded-xl
              bg-white/10 hover:bg-white/20
              border border-white/10
              transition-all duration-200
              flex items-center gap-2
              ${refreshing ? 'opacity-50' : ''}
            `}
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm font-medium">
              Actualizar
            </span>
          </button>}
        </div>
      </div>

      {/* Main */}
      <div className="relative z-10 p-4 sm:p-6 space-y-5">
        <div hidden={Boolean(selectedInventario)} className="space-y-5">
        {resumenError && <p role="alert" className="text-red-300">{resumenError}</p>}

        {/* PROVIDERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          {resumen.map((item) => (
            <button
              type="button"
              key={item.proveedor}
              aria-label={`Filtrar por ${item.proveedor}`}
              aria-pressed={filters.proveedor === item.proveedor}
              onClick={() => {
                setSelectedClient(null);
                setFilters({
                  ...filters,
                  proveedor: item.proveedor,
                  cliente: ''
                });
              }}
              className={`
                ${cardClasses}
                min-w-0 rounded-2xl p-4 text-left
                hover:border-violet-400/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400
                transition-colors
                ${filters.proveedor === item.proveedor ? 'ring-1 ring-violet-400 bg-violet-500/10' : ''}
              `}
            >
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-base font-semibold">{item.proveedor === 'OCI' ? 'Oracle' : item.proveedor}</h3>
                {getProveedorIcon(item.proveedor) ? (
                  <img
                    src={getProveedorIcon(item.proveedor)}
                    alt={item.proveedor}
                    className="w-8 h-8 object-contain"
                  />
                ) : (
                  <Globe className="w-8 h-8 opacity-60" />
                )}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Inventarios históricos</span>
                  <span className="font-semibold">
                    {item.total_snapshots.toLocaleString('es-MX')}
                  </span>
                </div>

                <div className="pt-3 border-t border-white/10">
                  <p className="text-xs text-white/60">
                    Última carga · México
                  </p>
                  <p className="text-xs mt-1 font-medium">
                    {item.ultima_actualizacion ? formatDate(item.ultima_actualizacion) : 'Sin cargas'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* FILTERS */}
        <div className={`${cardClasses} relative z-20 rounded-2xl p-4 sm:p-5`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-[#8644ff]/20 border border-[#8644ff]/20">
              <Filter className="w-5 h-5 text-[#cbb5f9]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                Explorar inventarios
              </h2>
              <p className="text-sm opacity-60">
                Filtra por nube, cliente y fechas. Zona horaria: Ciudad de México.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.3fr_1.5fr_1fr_1fr_auto] gap-3 items-end">
            <label className="min-w-0 space-y-1.5 text-xs text-white/70">
              <span>Nube</span>
            <select
              value={filters.proveedor}
              onChange={(e) => {
                setSelectedClient(null);
                setFilters({
                  ...filters,
                  proveedor: e.target.value,
                  cliente: ''
                });
              }}
              className={inputClasses}
            >
              <option value="">
                Todos los proveedores
              </option>
              {resumen.map((item) => (
                <option
                  key={item.proveedor}
                  value={item.proveedor}
                >
                  {item.proveedor === 'OCI' ? 'Oracle (OCI)' : item.proveedor}
                </option>
              ))}
            </select>
            </label>

            <div className="min-w-0">
              <SearchableSelect label="Cliente" options={clientOptions} value={selectedClient?.id || ''}
                placeholder="Buscar cliente..."
                text={selectedClient?.label || filters.cliente}
                loading={catalogLoading}
                onOpen={() => setCatalogOpen(true)}
                onTextChange={value => {
                  setSelectedClient(null);
                  setFilters({ ...filters, cliente: value });
                }}
                onSelect={option => {
                  setSelectedClient(option);
                  setFilters({ ...filters, cliente: option.name });
                }}
                className={inputClasses}
              />
              {catalogError && <p role="alert" className="text-xs text-red-300 mt-1">{catalogError}</p>}
            </div>

            <label className="min-w-0 space-y-1.5 text-xs text-white/70">
              <span>Desde</span>
            <input
              type="date"
              aria-label="Fecha inicial"
              value={filters.fechaInicio}
              onChange={(e) => {
                setSelectedClient(null);
                setFilters({
                  ...filters,
                  fechaInicio: e.target.value
                });
              }}
              className={inputClasses}
            />
            </label>

            <label className="min-w-0 space-y-1.5 text-xs text-white/70">
              <span>Hasta</span>
            <input
              type="date"
              aria-label="Fecha final"
              value={filters.fechaFin}
              onChange={(e) => {
                setSelectedClient(null);
                setFilters({
                  ...filters,
                  fechaFin: e.target.value
                });
              }}
              className={inputClasses}
            />
            </label>

            <button
              onClick={loadInventarios}
              className="
                rounded-xl text-sm
                bg-[#8644ff]
                hover:bg-[#6439c1]
                transition-all duration-200
                font-semibold
                px-5 py-2.5 border border-transparent
                flex items-center justify-center gap-2
              "
            >
              <Search className="w-4 h-4" />
              Buscar
            </button>
          </div>
        </div>

        {/* RESULTADOS */}
        {inventariosError && <p role="alert" className="text-red-300">{inventariosError}</p>}
        {loadingInventarios && (
          <p role="status" className="text-white/60">Buscando inventarios...</p>
        )}
        {inventarios.length > 0 && (
          <div className={`${cardClasses} rounded-2xl overflow-hidden`}>
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Resultados</h2>
                <p className="text-sm opacity-60">
                  {inventarios.length} inventario{inventarios.length !== 1 ? 's' : ''} encontrados
                </p>
              </div>
            </div>

            {loadingInventarios ? (
              <div className="flex items-center justify-center py-16 gap-3 text-white/60">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#8644ff]" />
                <span className="text-sm">Buscando inventarios...</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left">
                      <th className="px-6 py-3 text-xs uppercase tracking-widest opacity-50 font-medium">ID</th>
                      <th className="px-6 py-3 text-xs uppercase tracking-widest opacity-50 font-medium">Proveedor</th>
                      <th className="px-6 py-3 text-xs uppercase tracking-widest opacity-50 font-medium">Cliente</th>
                      <th className="px-6 py-3 text-xs uppercase tracking-widest opacity-50 font-medium">Región</th>
                      <th className="px-6 py-3 text-xs uppercase tracking-widest opacity-50 font-medium">Fecha (México)</th>
                      <th className="px-6 py-3 text-xs uppercase tracking-widest opacity-50 font-medium">Acción</th>
                    </tr>
                  </thead>

                  <tbody>
                    {inventarios.map((inv, idx) => (
                      <tr
                        key={`${inv.id}-${inv.region}-${idx}`}
                        onClick={() => loadDetalle(inv)}
                        className={`
                          border-b border-white/5
                          hover:bg-white/5
                          cursor-pointer
                          transition-colors duration-150
                          ${selectedInventario && captureKey(selectedInventario) === captureKey(inv) ? 'bg-[#8644ff]/10' : ''}
                          ${idx % 2 === 0 ? '' : 'bg-white/[0.02]'}
                        `}
                      >
                        <td className="px-6 py-4 opacity-40 font-mono text-xs">#{inv.id}</td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {getProveedorIcon(inv.proveedor) ? (
                              <img
                                src={getProveedorIcon(inv.proveedor)}
                                alt={inv.proveedor}
                                className="w-5 h-5 object-contain"
                              />
                            ) : null}
                            <span className="font-semibold">{inv.proveedor}</span>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium">
                              {inv.nombre_cliente || inv.nombre_cliente_legacy || '—'}
                            </p>
                            {inventoryAccount(inv) && <p className="mt-1 text-sm text-violet-200 break-words">
                              {inventoryAccount(inv)}
                            </p>}
                            {inv.cliente_sap && (
                              <p className="text-xs opacity-40 mt-0.5">SAP: {inv.cliente_sap}</p>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 opacity-70">
                          {inv.region || '—'}
                        </td>

                        <td className="px-6 py-4 opacity-70 font-mono text-xs">
                          {formatDate(inv.fecha_creacion)}
                        </td>

                        <td className="px-6 py-4">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              loadDetalle(inv); 
                            }}
                            className="
                              px-3 py-1.5 rounded-xl text-xs font-medium
                              bg-[#8644ff]/20 hover:bg-[#8644ff]/40
                              border border-[#8644ff]/30
                              transition-all duration-150
                            "
                          >
                            Ver detalle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* EMPTY STATE */}
        {!loadingInventarios && !inventariosError && inventarios.length === 0 && (
          <div className={`${cardClasses} rounded-2xl px-5 py-10 text-center`}>
            <Database className="w-8 h-8 text-violet-300/50 mx-auto mb-3" />
            <p className="text-base text-white/80">
              {hasDateRange ? 'No hay inventarios para el rango seleccionado.' : 'Aun no se han generado inventarios para hoy con los filtros seleccionados.'}
            </p>
            <p className="text-sm text-white/55 mt-2">
              {hasDateRange ? 'Ajusta los filtros y presiona Buscar.' : 'La consulta usa el dia actual de Mexico. Selecciona otro rango para consultar el historico.'}
            </p>
          </div>
        )}

        </div>

        {/* DETALLE */}
        {selectedInventario && (
            <section
              ref={detailView}
              tabIndex={-1}
              aria-label="Detalle del inventario"
              className="space-y-5 outline-none"
            >

              {/* Header */}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <button
                  onClick={closeDetalle}
                  className="inline-flex items-center gap-2 rounded-xl border border-violet-300/20 px-3 py-2 text-sm text-violet-100 hover:bg-violet-400/10 focus-visible:ring-2 focus-visible:ring-violet-400"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Volver a resultados
                </button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getProveedorIcon(selectedInventario.proveedor) && (
                      <img
                        src={getProveedorIcon(selectedInventario.proveedor)}
                        alt={selectedInventario.proveedor}
                        className="w-5 h-5 object-contain"
                      />
                    )}
                    <span className="font-bold text-lg">{selectedInventario.proveedor}</span>
                    <span className="text-xs opacity-40 font-mono">#{selectedInventario.id}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-violet-100 break-words">
                    {selectedInventario.nombre_cliente || selectedInventario.nombre_cliente_legacy || '—'}
                  </h3>
                  {inventoryAccount(selectedInventario) && <p className="mt-1 text-sm text-violet-200 break-words">
                    {inventoryAccount(selectedInventario)}
                  </p>}
                  {selectedInventario.cliente_sap && (
                    <p className="text-xs opacity-50 mt-0.5">SAP: {selectedInventario.cliente_sap}</p>
                  )}
                </div>
              </div>

              {/* Meta */}
              <div className={`${cardClasses} rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm`}>
                <div>
                  <p className="text-xs opacity-50 mb-1">Región</p>
                  <p className="font-medium">{selectedInventario.region || '—'}</p>
                </div>
                <div>
                  <p className="text-xs opacity-50 mb-1">Fecha de inventario (México)</p>
                  <p className="font-medium">{formatDate(selectedInventario.fecha_creacion)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-50 mb-1">Cuenta / identificador cloud</p>
                  <p className="font-mono text-xs break-all">{selectedInventario.numero_cliente || '—'}</p>
                </div>
              </div>

              <InventoryDownload key={captureKey(selectedInventario)} inventoryId={selectedInventario.id} inventory={selectedInventario} />

              <div className="bg-violet-500/10 border border-violet-300/20 rounded-2xl p-4 text-sm leading-6 text-violet-100">
                <p className="font-semibold mb-1">Captura histórica · No es una consulta en vivo</p>
                <p>
                  La descripción, los contadores y las filas de recursos reflejan la infraestructura al momento de la recolección del inventario. No muestran su estado actual ni constituyen un diagnóstico de salud.
                </p>
              </div>

              {/* BODY */}
              <div>
                {loadingDetalle ? (
                  <div role="status" className={`${cardClasses} rounded-2xl flex items-center justify-center min-h-[220px] gap-3 text-violet-100/80`}>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#8644ff]" />
                    <span className="text-sm">
                      Analizando inventario...
                    </span>
                  </div>
                ) : detalle ? (
                  <div className="space-y-5">

                    {/* RESUMEN */}
                    <div className="bg-violet-500/10 border border-violet-300/15 rounded-2xl p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Database className="w-5 h-5 text-[#cbb5f9]" />
                          <h4 className="font-semibold">
                            Resumen del Inventario
                          </h4>
                        </div>
                        <span className="
                          px-3 py-1 rounded-full text-xs font-semibold
                          bg-[#8644ff]/20 text-[#cbb5f9]
                          border border-[#8644ff]/20
                        ">
                          {detalle.metadata?.total_recursos || 0} recursos
                        </span>
                      </div>
                      <p className="mt-4 text-sm leading-7 text-violet-50/90">
                        {buildInventorySummary(
                          selectedInventario, detalle,
                          formatDate(selectedInventario.fecha_creacion)
                        )}
                      </p>
                      <p className="mt-2 text-xs leading-5 text-white/55">
                        Resumen automático basado en los conteos del inventario, sin IA.
                      </p>
                    </div>

                    {/* SERVICIOS */}
                    {detalle.por_servicio['Collection (OCI Coverage)'] && (
                      <details className={`${cardClasses} rounded-2xl p-4`}>
                        <summary className="cursor-pointer">Cobertura de recolección Oracle · regiones y compartimentos</summary>
                        <p className="text-sm text-white/60 my-3">Los fallos no representan cero recursos. El Excel incluye toda la cobertura.</p>
                        <ul className="space-y-1 text-xs break-all">
                          {detalle.por_servicio['Collection (OCI Coverage)'].coverage.slice(0, 200).map((item, index) => (
                            <li key={index} className={item.status === 'error' ? 'text-red-300' : 'text-white/70'}>
                              {item.kind} · {item.region} · {item.compartment_id}: {item.status === 'ok' ? 'Completado' : 'Error'}
                            </li>
                          ))}
                        </ul>
                        {detalle.por_servicio['Collection (OCI Coverage)'].coverage.length > 200 && (
                          <p className="text-xs mt-3">Vista previa de 200 consultas; consulta el Excel para el detalle completo.</p>
                        )}
                      </details>
                    )}
                    {Object.entries(detalle.por_servicio || {}).filter(([name]) => name !== 'Collection (OCI Coverage)').map(([servicio, dataSvc]) => {
                      const recursos = dataSvc.items || [];
                      const totalReal = dataSvc.total_real || recursos.length;
                      const isMachine = machineTypes[selectedInventario.proveedor?.toUpperCase()] === servicio;

                      return (
                        <div
                          key={servicio}
                          className="bg-black/30 rounded-2xl border border-white/5 overflow-hidden"
                        >
                          {/* HEADER SERVICIO */}
                          <button
                            onClick={() => toggleService(servicio)}
                            aria-expanded={Boolean(expandedServices[servicio])}
                            className="w-full px-4 py-3 flex items-center justify-between border-b border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-[#8644ff]" />
                              <span className="font-semibold text-sm uppercase tracking-wider text-[#cbb5f9]">
                                {servicio}
                              </span>
                            </div>

                            <span className="text-xs bg-[#8644ff]/20 text-[#cbb5f9] px-2 py-1 rounded-full border border-[#8644ff]/20">
                              {totalReal.toLocaleString()}
                            </span>
                          </button>

                          {/* TABLA */}
                          {expandedServices[servicio] && (
                            <div className="overflow-x-auto animate-in fade-in duration-200">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-white/5">
                                    <th className="px-4 py-3 text-left opacity-40 font-medium">Nombre</th>
                                    <th className="px-4 py-3 text-left opacity-40 font-medium">Tipo</th>
                                    <th className="px-4 py-3 text-left opacity-40 font-medium">Región</th>
                                    {isMachine && <>
                                      <th className="px-4 py-3 text-left opacity-40 font-medium">Estado</th>
                                      <th className="px-4 py-3 text-left opacity-40 font-medium">Tamaño / SKU</th>
                                    </>}
                                    <th className="px-4 py-3 text-left opacity-40 font-medium">Detalle</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {recursos.slice(0, 50).map((r, i) => {
                                    const machineKey = `${servicio}:${i}`;
                                    return <React.Fragment key={r.resource_id || i}>
                                    <tr
                                      className="border-b border-white/[0.03] hover:bg-white/5 transition-colors"
                                    >
                                      <td className="px-4 py-3 font-medium text-white/90">
                                        {r.resource_name || 'N/A'}
                                      </td>
                                      <td className="px-4 py-3 font-mono opacity-60">
                                        {r.resource_type || 'N/A'}
                                      </td>
                                      <td className="px-4 py-3 opacity-60">
                                        {r.region || 'global'}
                                      </td>
                                      {isMachine && <>
                                        <td className="px-4 py-3">{machineStateLabel(selectedInventario.proveedor, r.machine?.state)}</td>
                                        <td className="px-4 py-3">{r.machine?.size || 'No informado'}</td>
                                      </>}
                                        <td className="px-4 py-3">
                                          <button
                                            aria-expanded={Boolean(expandedMachines[machineKey])}
                                            aria-controls={`resource-${encodeURIComponent(servicio)}-${i}`}
                                            aria-label={`${expandedMachines[machineKey] ? 'Ocultar' : 'Ver'} ficha de ${r.resource_name || r.resource_id}`}
                                            onClick={() => setExpandedMachines((current) => ({ ...current, [machineKey]: !current[machineKey] }))}
                                            className="rounded-lg border border-violet-300/20 px-3 py-2 text-violet-200 hover:bg-violet-400/10 focus-visible:ring-2 focus-visible:ring-violet-400 whitespace-nowrap"
                                          >
                                            {expandedMachines[machineKey] ? 'Ocultar ficha' : 'Ver ficha'}
                                          </button>
                                        </td>
                                    </tr>
                                    {expandedMachines[machineKey] && <tr id={`resource-${encodeURIComponent(servicio)}-${i}`}>
                                      <td colSpan={isMachine ? 6 : 4}>
                                        {isMachine
                                          ? <InventoryMachineDetails machine={r.machine} provider={selectedInventario.proveedor} />
                                          : <InventoryResourceDetails resource={r} />}
                                      </td>
                                    </tr>}
                                    </React.Fragment>;
                                  })}
                                </tbody>
                              </table>

                              {totalReal > 50 && (
                                <div className="p-3 text-center text-xs opacity-50 bg-white/[0.01]">
                                  Mostrando 50 de {totalReal.toLocaleString()} recursos.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div role="alert" className={`${cardClasses} rounded-2xl p-6 text-red-300 text-sm`}>
                    {detalleError || 'No se pudo cargar el detalle'}
                  </div>
                )}
              </div>
            </section>
        )}

      </div>
    </div>
  );
}